import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Users, FileText, Clock, PlusCircle } from 'lucide-react';
import api from '@/api';
import toast from 'react-hot-toast';
import { HiOutlineCloudArrowDown } from "react-icons/hi2";
import RecentList from '../components/RecentList';
import Card from '../components/Card';
import { toArray } from '../utils/toArray';

// Composant pour les cartes de statistiques (cliquables ou avec info au survol)
const StatCard = ({
    label,
    value,
    icon,
    color,
    linkTo,
    isLoading,
    onClick,
    onMouseEnter,
    onMouseLeave,
    tooltip,
    tooltipPosition = 'right',
}) => {
    const classes =
        "relative bg-pro-dark-surface p-6 rounded-2xl shadow-lg flex items-center gap-4 transform hover:-translate-y-1 transition-transform duration-300";

    const content = (
        <>
            <div className={`p-3 rounded-full ${color} text-white`}>
                {icon}
            </div>
            <div className="text-left">
                {isLoading ? (
                    <div className="h-7 w-12 bg-pro-dark-border rounded-md animate-pulse"></div>
                ) : (
                    <div className="text-2xl font-bold text-left">{value}</div>
                )}
                <div className="text-sm text-pro-dark-text-secondary">{label}</div>
            </div>
            {tooltip && (
                <div
                    className={`pointer-events-none absolute top-1/2 -translate-y-1/2 bg-pro-dark-surface border border-pro-dark-border p-4 rounded-md shadow-lg w-64 opacity-0 group-hover:opacity-100 transition-opacity ${
                        tooltipPosition === 'left'
                            ? 'right-full mr-4'
                            : 'left-full ml-4'
                    }`}
                >
                    {tooltip}
                </div>
            )}
        </>
    );

    const wrapperProps = {
        className: `${classes} group`,
        onMouseEnter,
        onMouseLeave,
    };

    if (linkTo) {
        return (
            <Link to={linkTo} {...wrapperProps}>
                {content}
            </Link>
        );
    }


    if (onClick) {
        return (
            <button type="button" onClick={onClick} {...wrapperProps}>
                {content}
            </button>
        );
    }

    return (
        <div {...wrapperProps}>
            {content}
        </div>
    );
};

// Composant pour afficher une ligne dans les listes d'activité récente
const ActivityItem = ({ text, subtext, linkTo }) => (
    <Link to={linkTo} className="flex items-center justify-between p-3 rounded-lg hover:bg-pro-dark-border transition-colors">
        <div>
            <p className="font-medium text-pro-dark-text-primary">{text}</p>
            <p className="text-xs text-pro-dark-text-secondary">{subtext}</p>
        </div>
        <Clock size={14} className="text-pro-dark-text-secondary" />
    </Link>
);

const HomePage = () => {
    const [isSyncing, setIsSyncing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [lastSync, setLastSync] = useState(null);
    const [stats, setStats] = useState({
        candidatsCount: 0,
        offresCount: 0,
        matchesRealisesCount: 0,
        derniersCandidats: [],
        dernieresOffres: [],
    });
    const [isLoading, setIsLoading] = useState(true);
    const [showCoverageInfo, setShowCoverageInfo] = useState(false);
    const fetchStats = async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const response = await api.get('/dashboard/stats');
            setStats({
                candidatsCount: response.data.candidats_count,
                offresCount: response.data.offres_count,
                matchesRealisesCount: response.data.matches_realises_count,
                derniersCandidats: Array.isArray(response.data.derniers_candidats) ? response.data.derniers_candidats : [],
                dernieresOffres: Array.isArray(response.data.dernieres_offres) ? response.data.dernieres_offres : [],
            });
        } catch (error) {
            console.error("Erreur lors de la récupération des stats:", error);
            toast.error("Impossible de charger les statistiques du tableau de bord.");
            setHasError(true);
            setStats({
                candidatsCount: 0,
                offresCount: 0,
                matchesRealisesCount: 0,
                derniersCandidats: [],
                dernieresOffres: [],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        let interval;
        if (isSyncing) {
            setProgress(0);
            interval = setInterval(() => {
                setProgress((prev) => (prev >= 100 ? 0 : prev + 10));
            }, 200);
        }
        return () => clearInterval(interval);
    }, [isSyncing]);

    const coveredOffers = Math.round((stats.matchesRealisesCount / 100) * stats.offresCount);

    const statCards = [
        { label: "Candidats", value: stats.candidatsCount, icon: <Users size={20} />, color: "bg-blue-500", linkTo: "/candidats" },
        { label: "Offres Ouvertes", value: stats.offresCount, icon: <FileText size={20} />, color: "bg-green-500", linkTo: "/offres" },
        {
            label: "Taux de couverture (%)",
            value: stats.matchesRealisesCount,
            icon: <BarChart3 size={20} />,
            color: "bg-purple-500",
            tooltip: (
                <div className="text-sm text-pro-dark-text-primary">
                    <p className="mb-2">Ce pourcentage indique la part des appels d'offres disposant d'au moins un candidat correspondant.</p>
                    <div className="space-y-1">
                        <p><strong>Total d'offres:</strong> {stats.offresCount}</p>
                        <p><strong>Offres avec candidat compatible:</strong> {coveredOffers}</p>
                        <p><strong>Taux de couverture:</strong> {stats.matchesRealisesCount}%</p>
                    </div>
                    <p className="text-xs text-pro-dark-text-secondary mt-2">Calcul: ({coveredOffers} / {stats.offresCount}) × 100 = {stats.matchesRealisesCount}%</p>
                </div>
            ),
            tooltipPosition: 'left',
        },
    ];

    const handleSync = async () => {
        const importOnlyVisible = window.confirm(
            "Souhaitez-vous importer uniquement les lignes non masquées de la Google Sheet ?\n" +
                "OK : importer uniquement les lignes visibles (par défaut)\n" +
                "Annuler : importer toutes les lignes, y compris celles masquées."
        );
        const includeHidden = !importOnlyVisible;
        setIsSyncing(true);
        const syncToast = toast.loading("Import depuis Google Sheets en cours...");
        try {
            const response = await api.post('/sync/google-sheets-import', { include_hidden: includeHidden });
            const details = response.data.details;
            toast.success(
                `Import terminé ! \nCandidats ajoutés: ${details.candidats.added}, mis à jour: ${details.candidats.updated}.\nAppels d'offre ajoutés: ${details.offres.added}, mis à jour: ${details.offres.updated}.`,
                { id: syncToast, duration: 6000 }
            );
            await fetchStats();
            setLastSync(new Date().toLocaleString());
        } catch (error) {
            console.error("Erreur de synchronisation:", error);
            let message;
            if (error.response) {
                message = error.response.data?.detail || `Erreur: ${error.response.status}`;
            } else {
                message = "Serveur indisponible, veuillez réessayer plus tard.";
            }
            toast.error(message, { id: syncToast });
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="animate-fade-in space-y-12">
            <div>
                <h1 className="text-4xl font-bold mb-2 text-pro-dark-text-primary">Tableau de Bord</h1>
                <p className="text-lg text-pro-dark-text-secondary">
                    Bienvenue sur votre outil de matching intelligent.
                </p>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={handleSync}
                    disabled={isSyncing}
                    className="flex items-center gap-2 px-4 py-2 bg-pro-dark-surface border border-pro-dark-border text-white rounded-lg hover:bg-pro-dark-border disabled:opacity-50 transition-colors">
                    <HiOutlineCloudArrowDown className={isSyncing ? 'animate-spin' : ''} />
                    {isSyncing ? (
                        <progress className="w-24" max="100" value={progress} />
                    ) : (
                        'Importer depuis Google Sheets'
                    )}
                </button>
                {lastSync && (
                    <span className="text-sm text-pro-dark-text-secondary">Dernière sync : {lastSync}</span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {hasError ? (
                    <Card>Impossible de charger…</Card>
                ) : (
                    statCards.map((card) => (
                        <StatCard key={card.label} {...card} isLoading={isLoading} />
                    ))
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <RecentList
                    title="Derniers candidats ajoutés"
                    items={stats.derniersCandidats}
                    isLoading={isLoading}
                    addLink="/candidats/ajouter"
                    renderItem={(candidat) => (
                        <ActivityItem
                            key={candidat.id}
                            text={`${candidat.prenom} ${candidat.nom}`}
                            subtext={candidat.posteActuel}
                            linkTo={`/candidats/modifier/${candidat.id}`}
                        />
                    )}
                />
                <RecentList
                    title="Dernières offres ajoutées"
                    items={stats.dernieresOffres}
                    isLoading={isLoading}
                    addLink="/offres/ajouter"
                    renderItem={(offre) => (
                        <ActivityItem
                            key={offre.id}
                            text={offre.detailDuBesoin}
                            subtext={offre.compte}
                            linkTo={`/offres/modifier/${offre.id}`}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default HomePage;
